import {useState} from "react";
import {Error, GroupFilterType, MutationDeleteCallGroupArgs, ReferenceRetarget} from "@/api";
import {useDispatch} from "react-redux";
import {useMutation} from "@apollo/client";
import {showNotification} from "@/actions";
import {useNavigate, NavigateFunction} from "react-router";
import {
    DeleteCallGroupDocument,
    DeleteCallGroupMutation,
    DeleteCallGroupMutationHookResult,
    DeleteCallGroupMutationVariables,
} from "@/components/call-group/mutations.generated";
import {GroupTypeDesc, groupTypesDesc} from "@/components/call-group/desc";
import {defineMessage} from "react-intl";

const initialState: MutationDeleteCallGroupArgs = {
    id: null,
    relatedData: {
        gateways: [],
        users: [],
        ivrs: [],
    },
};

export enum ReferenceType {
    GATEWAY = "gateways",
    USER = "users",
    IVR = "ivrs",
}

type Selection = {
    [id: number]: number;
};

export default class DeleteCallGroupForm {
    domainId: number;
    groupId: number;
    groupType: GroupFilterType;
    values: MutationDeleteCallGroupArgs;
    errors: Error[];
    selections: Selection;
    desc: GroupTypeDesc;

    protected _setValues: (data: MutationDeleteCallGroupArgs) => void;
    protected setErrors: (errors: Error[]) => void;
    protected setSelections: (selections: Selection) => void;
    protected dispatch: any;
    protected mutation: DeleteCallGroupMutationHookResult[0];
    protected navigate: NavigateFunction;

    constructor(domainId: number, groupId: number, groupType: GroupFilterType) {
        [this.mutation] = useMutation<DeleteCallGroupMutation, DeleteCallGroupMutationVariables>(
            DeleteCallGroupDocument,
        );
        [this.errors, this.setErrors] = useState([]);
        [this.values, this._setValues] = useState(initialState);
        [this.selections, this.setSelections] = useState<Selection>({});
        this.domainId = domainId;
        this.groupId = groupId;
        this.groupType = groupType;
        this.dispatch = useDispatch();
        this.navigate = useNavigate();
        this.desc = groupTypesDesc[this.groupType];
    }

    redirect() {
        this.navigate(`/domains/${this.domainId}/${this.desc.itemPath}`);
    }

    updateValues(data: Partial<MutationDeleteCallGroupArgs>) {
        this._setValues(Object.assign({}, this.values, data));
    }

    selectNewTarget(referrerId: number, type: ReferenceType, newTargetId: number) {
        const list: ReferenceRetarget[] = this.values.relatedData[type];
        const found = list.find((e) => e.referrerId == referrerId);
        if (found) found.newTargetNumberId = newTargetId;
        else
            list.push({
                referrerId: referrerId,
                newTargetNumberId: newTargetId,
            });
        this._setValues(Object.assign({}, this.values, this.values));
    }

    deselectTarget(referrerId: number, type: ReferenceType) {
        const list: ReferenceRetarget[] = this.values.relatedData[type];
        const index = list.findIndex((e) => e.referrerId == referrerId);
        if (index > -1) list.splice(index, 1);
    }

    handleChange(newTarget: number, dataId: number, dataType: ReferenceType): void {
        if (newTarget === 0) {
            this.deselectTarget(dataId, dataType);
        } else {
            this.selectNewTarget(dataId, dataType, newTarget);
        }
        this.setSelections({
            ...this.selections,
            [dataId]: newTarget,
        });
    }

    async delete() {
        const result = await this.mutation({
            variables: {
                id: this.groupId,
                relatedData: this.values.relatedData,
            },
        });

        if (result.errors || result.data.deleteCallGroup.errors) {
            this.dispatch(
                showNotification(defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}), "error"),
            );
            return;
        } else {
            this.dispatch(showNotification(this.desc.deleteNotification, "success"));
            this.redirect();
            return;
        }
    }

    reset() {
        this.updateValues(initialState);
        this.setSelections({});
    }
}
